import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BiArrowBack, BiPlus, BiTrash, BiEdit, BiSave, BiX, BiSearch } from "react-icons/bi";
import { Colors } from "../../../assets/Colors";

const API = process.env.REACT_APP_API_URL;
const authHeaders = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return { Authorization: "Bearer " + user.Token };
};

const SATUAN = [
  { value: "gram", label: "gram" },
  { value: "ml", label: "ml" },
  { value: "pcs", label: "pcs" },
];
const KATEGORI = ["Makanan Utama", "Lauk", "Sayur", "Sambal & Pelengkap", "Snack", "Minuman", "Dessert", "Lainnya"];

const nf = (n) => new Intl.NumberFormat("id-ID", { maximumFractionDigits: 2 }).format(Number(n) || 0);
const barisKosong = () => ({ BarangID: "", Gramasi: "", SatuanGramasi: "gram", Keterangan: "" });
const formKosong = { ResepID: 0, NamaMenu: "", Kategori: "", Keterangan: "", Bahan: [barisKosong()] };

// Resep = nama menu makanan + komposisi bahan baku (MBarang berjenis Logistik) + gramasi per 1 porsi.
export default function ResepPage() {
  const navigate = useNavigate();
  const [daftar, setDaftar] = useState([]);
  const [bahanList, setBahanList] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(null); // null = tampilkan daftar
  const [saving, setSaving] = useState(false);
  const [porsi, setPorsi] = useState(1);

  const loadList = useCallback(() => {
    axios.get(API + "getResepList", { params: { search }, headers: authHeaders() })
      .then((r) => setDaftar(r.data?.data ?? []))
      .catch((e) => { setDaftar([]); toast.error(e.response?.data?.message || "Gagal memuat resep"); });
  }, [search]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    axios.get(API + "getBahanLogistik", { headers: authHeaders() })
      .then((r) => setBahanList(r.data?.data ?? []))
      .catch(() => setBahanList([]));
  }, []);

  const opsiBahan = useMemo(
    () => bahanList.map((b) => ({ value: b.BarangID, label: `${b.NamaBarang}${b.SatuanBeli ? " (" + b.SatuanBeli + ")" : ""}` })),
    [bahanList]
  );
  const infoBahan = useMemo(() => Object.fromEntries(bahanList.map((b) => [b.BarangID, b])), [bahanList]);

  const buka = async (id) => {
    setPorsi(1);
    if (!id) { setForm({ ...formKosong, Bahan: [barisKosong()] }); return; }
    try {
      const r = await axios.get(API + "getResepDetail", { params: { id }, headers: authHeaders() });
      const d = r.data.data;
      setForm({ ...d, Bahan: d.Bahan.length ? d.Bahan : [barisKosong()] });
    } catch (e) { toast.error(e.response?.data?.message || "Gagal memuat resep"); }
  };

  const setBahan = (i, k, v) => setForm((f) => ({ ...f, Bahan: f.Bahan.map((b, x) => (x === i ? { ...b, [k]: v } : b)) }));
  const tambahBaris = () => setForm((f) => ({ ...f, Bahan: [...f.Bahan, barisKosong()] }));
  const hapusBaris = (i) => setForm((f) => ({ ...f, Bahan: f.Bahan.length > 1 ? f.Bahan.filter((_, x) => x !== i) : f.Bahan }));

  const simpan = async () => {
    if (!form.NamaMenu.trim()) return toast.error("Nama menu wajib diisi");
    const terisi = form.Bahan.filter((b) => b.BarangID || b.Gramasi !== "");
    if (!terisi.length) return toast.error("Isi minimal satu bahan baku");
    for (const [i, b] of terisi.entries()) {
      if (!b.BarangID) return toast.error(`Bahan baris ${i + 1} belum dipilih`);
      if (!(Number(b.Gramasi) > 0)) return toast.error(`Gramasi baris ${i + 1} harus lebih dari 0`);
    }
    setSaving(true);
    try {
      await axios.post(API + "saveResep", {
        ResepID: form.ResepID || 0,
        NamaMenu: form.NamaMenu.trim(),
        Kategori: form.Kategori || "",
        Keterangan: form.Keterangan || "",
        Bahan: terisi.map((b) => ({
          BarangID: b.BarangID, Gramasi: Number(b.Gramasi), SatuanGramasi: b.SatuanGramasi, Keterangan: b.Keterangan || "",
        })),
      }, { headers: authHeaders() });
      toast.success("Resep tersimpan");
      setForm(null);
      loadList();
    } catch (e) { toast.error(e.response?.data?.message || "Gagal menyimpan resep"); }
    finally { setSaving(false); }
  };

  const hapus = async (r) => {
    if (!window.confirm(`Hapus resep "${r.NamaMenu}"?`)) return;
    try {
      await axios.post(API + "deleteResep", { ResepID: r.ResepID }, { headers: authHeaders() });
      toast.success("Resep dihapus");
      loadList();
    } catch (e) { toast.error(e.response?.data?.message || "Gagal menghapus"); }
  };

  const input = "w-full border rounded-lg px-3 py-2 text-sm outline-none focus:border-orange-400";
  const lbl = "block text-xs font-bold uppercase text-gray-600 mb-1";

  // ── Form tambah / ubah ──
  if (form) {
    const totalGram = form.Bahan.reduce((t, b) => t + (b.SatuanGramasi === "gram" ? Number(b.Gramasi) || 0 : 0), 0);
    return (
      <div className="md:ml-64 px-4 md:px-6 pb-20">
        <div className="flex items-center justify-between py-4">
          <button onClick={() => setForm(null)} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
            <BiArrowBack className="text-lg" /> Kembali ke daftar
          </button>
          <span className="text-sm font-semibold" style={{ color: Colors.primary }}>
            {form.ResepID ? `Ubah Resep ${form.KodeResep || ""}` : "Resep Baru"}
          </span>
        </div>

        <div className="bg-white rounded-xl border p-5 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className={lbl}>Nama Menu <span className="text-red-500">*</span></label>
              <input className={input} value={form.NamaMenu} placeholder="mis. Ayam Goreng Kremes"
                onChange={(e) => setForm({ ...form, NamaMenu: e.target.value })} />
            </div>
            <div>
              <label className={lbl}>Kategori</label>
              <select className={input + " bg-white"} value={form.Kategori} onChange={(e) => setForm({ ...form, Kategori: e.target.value })}>
                <option value="">— Pilih —</option>
                {KATEGORI.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className={lbl}>Keterangan / Cara Penyajian</label>
              <textarea className={input} rows={2} value={form.Keterangan || ""}
                onChange={(e) => setForm({ ...form, Keterangan: e.target.value })} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-5 py-3 border-b flex flex-wrap items-center justify-between gap-2"
            style={{ background: Colors.primary + "15", color: Colors.primary }}>
            <span className="font-semibold text-sm">Komposisi Bahan Baku (takaran per 1 porsi)</span>
            <div className="flex items-center gap-2 text-xs text-gray-700">
              <span>Hitung kebutuhan untuk</span>
              <input type="number" min="1" className="w-20 border rounded px-2 py-1 text-right bg-white" value={porsi}
                onChange={(e) => setPorsi(Math.max(1, Number(e.target.value) || 1))} />
              <span>porsi</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100 text-gray-600 text-xs">
                  <th className="px-3 py-2 text-left font-semibold border border-gray-300 w-10">No</th>
                  <th className="px-3 py-2 text-left font-semibold border border-gray-300 min-w-[260px]">Bahan (Logistik)</th>
                  <th className="px-3 py-2 text-right font-semibold border border-gray-300 w-32">Gramasi / porsi</th>
                  <th className="px-3 py-2 text-left font-semibold border border-gray-300 w-28">Satuan</th>
                  <th className="px-3 py-2 text-left font-semibold border border-gray-300 min-w-[160px]">Keterangan</th>
                  <th className="px-3 py-2 text-right font-semibold border border-gray-300 w-40">Kebutuhan {porsi} porsi</th>
                  <th className="px-3 py-2 border border-gray-300 w-10" />
                </tr>
              </thead>
              <tbody>
                {form.Bahan.map((b, i) => (
                  <tr key={i}>
                    <td className="px-3 py-1.5 border border-gray-300 text-center">{i + 1}</td>
                    <td className="px-2 py-1.5 border border-gray-300">
                      <Select menuPortalTarget={document.body} placeholder="Cari bahan…" options={opsiBahan}
                        value={opsiBahan.find((o) => o.value === b.BarangID) || null}
                        onChange={(o) => setBahan(i, "BarangID", o ? o.value : "")} />
                      {b.BarangID && infoBahan[b.BarangID]?.SatuanBeli && (
                        <p className="text-[11px] text-gray-400 mt-0.5">Satuan beli: {infoBahan[b.BarangID].SatuanBeli}</p>
                      )}
                    </td>
                    <td className="px-2 py-1.5 border border-gray-300">
                      <input type="number" min="0" step="any" className="w-full border rounded px-2 py-1.5 text-right"
                        value={b.Gramasi} onChange={(e) => setBahan(i, "Gramasi", e.target.value)} />
                    </td>
                    <td className="px-2 py-1.5 border border-gray-300">
                      <select className="w-full border rounded px-2 py-1.5 bg-white" value={b.SatuanGramasi}
                        onChange={(e) => setBahan(i, "SatuanGramasi", e.target.value)}>
                        {SATUAN.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1.5 border border-gray-300">
                      <input className="w-full border rounded px-2 py-1.5" value={b.Keterangan || ""}
                        onChange={(e) => setBahan(i, "Keterangan", e.target.value)} />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-300 text-right font-medium whitespace-nowrap">
                      {Number(b.Gramasi) > 0 ? `${nf(Number(b.Gramasi) * porsi)} ${b.SatuanGramasi}` : "—"}
                    </td>
                    <td className="px-2 py-1.5 border border-gray-300 text-center">
                      <button onClick={() => hapusBaris(i)} className="text-red-400 hover:text-red-600 p-1" title="Hapus baris"><BiTrash /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-t">
            <button onClick={tambahBaris} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-blue-300 text-blue-700 text-sm hover:bg-blue-50">
              <BiPlus /> Tambah Bahan
            </button>
            <span className="text-xs text-gray-500">
              Berat total (hanya bahan bersatuan gram): <strong>{nf(totalGram)} gram</strong> per porsi
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={() => setForm(null)} className="flex items-center gap-1 px-4 py-2 rounded-lg border text-sm text-gray-600"><BiX /> Batal</button>
          <button onClick={simpan} disabled={saving}
            className="flex items-center gap-1 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
            style={{ background: Colors.primary }}>
            <BiSave /> {saving ? "Menyimpan…" : "Simpan Resep"}
          </button>
        </div>
      </div>
    );
  }

  // ── Daftar ──
  return (
    <div className="md:ml-64 px-4 md:px-6 pb-20">
      <div className="flex items-center justify-between py-4">
        <button onClick={() => navigate("/general-affair")} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <BiArrowBack className="text-lg" /> Kembali
        </button>
        <span className="text-sm font-semibold" style={{ color: Colors.primary }}>Resep</span>
      </div>

      <div className="bg-white rounded-xl border p-4 mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="relative">
          <BiSearch className="absolute left-3 top-3 text-gray-400" />
          <input className="border rounded-lg pl-9 pr-3 py-2 text-sm w-72 outline-none focus:border-orange-400"
            placeholder="Cari nama menu / kategori / kode…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <button onClick={() => buka(0)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-semibold"
          style={{ background: Colors.primary }}>
          <BiPlus /> Resep Baru
        </button>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        {daftar.length === 0 ? (
          <div className="py-14 text-center text-gray-400 text-sm">Belum ada resep. Klik <strong>Resep Baru</strong> untuk membuat.</div>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100 text-gray-600 text-xs">
                <th className="px-3 py-2 text-left font-semibold border border-gray-300">Kode</th>
                <th className="px-3 py-2 text-left font-semibold border border-gray-300">Nama Menu</th>
                <th className="px-3 py-2 text-left font-semibold border border-gray-300">Kategori</th>
                <th className="px-3 py-2 text-center font-semibold border border-gray-300">Jumlah Bahan</th>
                <th className="px-3 py-2 border border-gray-300 w-24" />
              </tr>
            </thead>
            <tbody>
              {daftar.map((r) => (
                <tr key={r.ResepID} className="hover:bg-orange-50">
                  <td className="px-3 py-2 border border-gray-300 font-mono text-xs">{r.KodeResep}</td>
                  <td className="px-3 py-2 border border-gray-300 font-medium">{r.NamaMenu}</td>
                  <td className="px-3 py-2 border border-gray-300">{r.Kategori || "—"}</td>
                  <td className="px-3 py-2 border border-gray-300 text-center">{r.JumlahBahan}</td>
                  <td className="px-3 py-2 border border-gray-300 text-center">
                    <button onClick={() => buka(r.ResepID)} className="text-blue-500 hover:text-blue-700 p-1" title="Lihat / ubah"><BiEdit /></button>
                    <button onClick={() => hapus(r)} className="text-red-400 hover:text-red-600 p-1" title="Hapus"><BiTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
